import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { resolveImageUrl } from '../lib/api';
import { useLang } from '../hooks/useLang';
import { pickLocalized } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Actualites = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/news`);
        if (!response.ok) throw new Error('Failed to fetch news');
        const data = await response.json();
        setNewsItems(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-grow pt-32 pb-20 px-8 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-wide mb-4">{t('news.title')}</h1>
          <p className="text-lg font-light text-gray-600 mb-16">{t('news.subtitle')}</p>

          {loading && <div className="text-center py-20"><p className="text-gray-500 font-light">{t('common.loading')}</p></div>}
          {error && <div className="text-center py-20"><p className="text-red-500 font-light">{t('common.error')}: {error}</p></div>}

          {!loading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {newsItems.map((item) => {
                const title = pickLocalized(item, 'title', lang);
                const category = pickLocalized(item, 'category', lang);
                return (
                  <Link key={item.id} to={item.link} className="group cursor-pointer">
                    <div className="relative aspect-[4/3] overflow-hidden mb-4">
                      <img src={resolveImageUrl(item.image)} alt={title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="flex-grow">
                        <p className="text-xs font-light tracking-wider text-gray-500 mb-2 uppercase">{category}</p>
                        <h3 className="text-xl font-light mb-1 group-hover:text-[#d94e47] transition-colors duration-300">{title}</h3>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#d94e47] transition-colors duration-300 mt-1 flex-shrink-0" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {newsItems.length === 0 && !loading && !error && (
            <div className="text-center py-20"><p className="text-gray-500 font-light">{t('news.empty')}</p></div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Actualites;
